import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/App";
import { useLanguage } from "@/i18n/LanguageContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Loader2, Search, Plus, Trash2, Sparkles, Database,
  ChevronLeft, ChevronRight, Check, X, RefreshCw, Tag, Pencil,
  Dna, ArrowUpRight, ArrowDownRight, Minus,
} from "lucide-react";
import FoodGapsWidget from "./FoodGapsWidget";  // iter723 — curation queue
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";

/**
 * NutramiManager — the admin's curation cockpit for building the Nutrami
 * clinical-grade food database out of USDA source records.
 *
 * Layout:
 *   ┌─────────────────────────┬──────────────────────────┐
 *   │  USDA Browser (23k)     │  Nutrami DB (curated)     │
 *   │  search + paginate      │  filter by category/tag   │
 *   │  → click ➕ to import    │  → click 🗑 to remove      │
 *   └─────────────────────────┴──────────────────────────┘
 */
export default function NutramiManager() {
  const { API } = useAuth();
  const { lang } = useLanguage();
  const bg = lang === "bg";

  const [categories, setCategories] = useState([]);
  const [tags, setTags] = useState([]);
  const [stats, setStats] = useState({ total: 0, by_category: [], by_tag: [] });

  useEffect(() => {
    (async () => {
      try {
        const [tax, s] = await Promise.all([
          axios.get(`${API}/admin/nutrami/categories`),
          axios.get(`${API}/admin/nutrami/stats`),
        ]);
        setCategories(tax.data.categories || []);
        setTags(tax.data.tags || []);
        setStats(s.data || {});
      } catch (err) {
        toast.error((bg ? "Грешка при зареждане: " : "Load failed: ") + (err?.response?.data?.detail || err.message));
      }
    })();
  }, [API, bg]);

  const refreshStats = useCallback(async () => {
    try { const s = await axios.get(`${API}/admin/nutrami/stats`); setStats(s.data || {}); } catch {} // eslint-disable-line
  }, [API]);

  const [addModal, setAddModal] = useState(null); // { fdc_id, name_en, ...usda_row }
  const [editModal, setEditModal] = useState(null); // full nutrami record
  const [manualModal, setManualModal] = useState(false); // iter955 — manual add flow
  const [csvModal, setCsvModal] = useState(false);       // iter956 — bulk CSV import

  return (
    <div className="space-y-4" data-testid="nutrami-manager">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Sparkles className="w-6 h-6 text-emerald-600" />
            {bg ? "Nutrami — Курирана база с храни" : "Nutrami — Curated Food Database"}
          </h2>
          <p className="text-xs text-muted-foreground mt-1 max-w-2xl">
            {bg
              ? "Изберете храни от USDA (23,600 записа) и ги добавете в Nutrami базата с точен nutrient panel — макро, минерали, витамини, мастни киселини. Всяка храна автоматично получава прогнозен инсулинов индекс и възпалителен индекс."
              : "Pick USDA records (23,600) and import them into Nutrami with the full nutrient panel — macros, minerals, vitamins, fatty acids. Each food auto-computes predicted insulin and inflammation indices."}
          </p>
        </div>
        <div className="text-right">
          <div className="text-3xl font-bold text-emerald-600" data-testid="nutrami-total">{stats.total || 0}</div>
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
            {bg ? "храни в Nutrami" : "foods in Nutrami"}
          </div>
          <Button
            variant="outline" size="sm"
            className="mt-2 text-[10px] h-7"
            data-testid="recompute-all-btn"
            onClick={async () => {
              if (!window.confirm(bg ? "Ще преизчисли NIS + DIL за всички записи. Продължаваш?" : "Recompute NIS + DIL for all records. Continue?")) return;
              try {
                const r = await axios.post(`${API}/admin/nutrami/recompute-all?refetch_usda=true`);
                toast.success(bg
                  ? `Обновени: ${r.data.updated}, USDA refetch: ${r.data.kcal_refetched}`
                  : `Updated: ${r.data.updated}, USDA refetched: ${r.data.kcal_refetched}`);
                refreshStats();
              } catch (err) {
                toast.error(err?.response?.data?.detail || (bg ? "Грешка." : "Error."));
              }
            }}
          >
            <RefreshCw className="w-3 h-3 mr-1" /> {bg ? "Преизчисли всички" : "Recompute all"}
          </Button>
        </div>
      </div>

      {stats.by_category?.length > 0 && (
        <div className="flex flex-wrap gap-2" data-testid="nutrami-cat-stats">
          {stats.by_category.map(c => {
            const cat = categories.find(x => x.key === c.category);
            return (
              <span key={c.category} className="px-2.5 py-1 rounded-full text-[11px] bg-emerald-50 border border-emerald-200 text-emerald-800">
                {(bg ? cat?.label_bg : cat?.label_en) || c.category}: <b>{c.count}</b>
              </span>
            );
          })}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <USDABrowser
          API={API} bg={bg}
          onImport={(row) => setAddModal(row)}
          onManualAdd={() => setManualModal(true)}
          onBulkCsv={() => setCsvModal(true)}
        />
        <NutramiList API={API} bg={bg} categories={categories} tags={tags} onChanged={refreshStats} onEdit={setEditModal} />
      </div>

      {/* iter723 — Data-driven curation queue */}
      <FoodGapsWidget API={API} bg={bg} onPromote={(row) => setAddModal(row)} />

      {addModal && (
        <AddFromUSDAModal
          API={API} bg={bg}
          categories={categories} tags={tags}
          usdaRow={addModal}
          onClose={() => setAddModal(null)}
          onSuccess={async () => {
            // If the addModal originated from a Gap row, mark that gap "promoted".
            if (addModal?._gap_id) {
              try {
                await axios.patch(`${API}/admin/food-gaps/${addModal._gap_id}`, { status: "promoted" });
              } catch { /* non-fatal */ }
            }
            setAddModal(null);
            refreshStats();
          }}
        />
      )}

      {editModal && (
        <EditNutramiModal
          API={API} bg={bg}
          categories={categories} tags={tags}
          record={editModal}
          onClose={() => setEditModal(null)}
          onSuccess={() => { setEditModal(null); refreshStats(); }}
        />
      )}

      {manualModal && (
        <ManualAddModal
          API={API} bg={bg}
          categories={categories} tags={tags}
          onClose={() => setManualModal(false)}
          onSuccess={() => { setManualModal(false); refreshStats(); }}
        />
      )}

      {csvModal && (
        <BulkCsvModal
          API={API} bg={bg}
          onClose={() => setCsvModal(false)}
          onSuccess={() => { refreshStats(); }}
        />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// USDA Browser (left column)
// ─────────────────────────────────────────────────────────────────────────
/**
 * useNameDuplicateCheck — debounced backend check as the admin types.
 * iter956. Returns ``{loading, exists, matches}`` where matches is the
 * short list of existing records with the same name_bg (optionally
 * scoped to a category).
 */
function useNameDuplicateCheck(API, name, category) {
  const [state, setState] = useState({ loading: false, exists: false, matches: [] });
  const timerRef = useRef(null);
  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    const trimmed = (name || "").trim();
    if (trimmed.length < 2) {
      setState({ loading: false, exists: false, matches: [] });
      return;
    }
    timerRef.current = setTimeout(async () => {
      setState(s => ({ ...s, loading: true }));
      try {
        const r = await axios.get(`${API}/admin/nutrami/check-duplicate`, {
          params: { name: trimmed, category: category || "" },
        });
        setState({
          loading:  false,
          exists:   !!r.data?.exists,
          matches:  r.data?.matches || [],
        });
      } catch {
        setState({ loading: false, exists: false, matches: [] });
      }
    }, 400);
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, [API, name, category]);
  return state;
}

/**
 * DuplicateWarning — inline banner shown under the name input when a
 * matching record already exists. Rendered only when exists === true.
 */
function DuplicateWarning({ bg, dupState }) {
  if (!dupState.exists || !dupState.matches?.length) return null;
  return (
    <div
      className="mt-1.5 p-2 rounded-md bg-red-50 border border-red-200 text-red-800 text-[10.5px] flex items-start gap-1.5"
      data-testid="duplicate-warning-banner"
    >
      <X className="w-3 h-3 mt-0.5 shrink-0" />
      <div className="min-w-0">
        <div className="font-bold">
          {bg ? "Тази храна вече съществува в Nutrami!" : "This food already exists in Nutrami!"}
        </div>
        {dupState.matches.slice(0, 3).map(m => (
          <div key={m.id} className="truncate">
            · <span className="font-semibold">{m.name_bg}</span>
            <span className="ml-1 opacity-70">({m.primary_category} · {m.source || "?"})</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * UsdaPreviewBadge — quick NIS/DIL estimate on cached USDA macros.
 * Uses the same DIL formula as the platform: 0.69·P + max(0, C-fiber).
 * NIS is a rough Atwater-normalized OLS proxy — final NIS is computed
 * server-side with the official pipeline on import, so this is only a
 * "at a glance" indicator to help the admin pick well.
 */
function UsdaPreviewBadge({ macros }) {
  const p = Number(macros?.protein) || 0;
  const c = Number(macros?.carbs) || 0;
  const f = Number(macros?.fat) || 0;
  const fib = Number(macros?.fiber) || 0;
  const kcal = Number(macros?.calories) || 0;

  const dil = (0.69 * p) + Math.max(0, c - fib);
  // Rough NIS proxy: normalise per 1000 kJ (Atwater 17C+17P+37F kJ/g) ~
  // (1.61C + 0.66P + 1.20F - 1.14fib) / kJ * 1000
  const kj = 4.184 * kcal || 1;
  const nis = kj > 100
    ? Math.max(0, Math.min(100, (1.61 * c + 0.66 * p + 1.20 * f - 1.14 * fib) / kj * 1000 * 0.7275 + 8.111))
    : null;

  const nisColor = nis == null ? "#9CA3AF" :
    nis >= 65 ? "#F97316" :
    nis >= 45 ? "#F59E0B" :
    nis >= 25 ? "#34D399" : "#10B981";
  const dilColor = dil >= 40 ? "#F97316" :
                   dil >= 25 ? "#F59E0B" :
                   dil >= 12 ? "#34D399" : "#10B981";

  return (
    <>
      {nis != null && (
        <span
          className="px-1.5 py-0.5 rounded text-[9px] font-bold text-white tabular-nums"
          style={{ background: nisColor }}
          title="Приблизителен NIS (при импорт се преизчислява)"
          data-testid={`usda-nis-preview-${macros?.fdc_id}`}
        >
          NIS≈{nis.toFixed(0)}%
        </span>
      )}
      <span
        className="px-1.5 py-0.5 rounded text-[9px] font-bold text-white tabular-nums"
        style={{ background: dilColor }}
        title="DIL (Dietary Insulin Load, g)"
        data-testid={`usda-dil-preview-${macros?.fdc_id}`}
      >
        DIL{dil.toFixed(1)}
      </span>
    </>
  );
}

function USDABrowser({ API, bg, onImport, onManualAdd, onBulkCsv }) {
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("");
  const [page, setPage] = useState(1);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [onlyNew, setOnlyNew] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/nutrami/usda-browse`, {
        params: { q, category, page, limit: 25, only_new: onlyNew },
      });
      setItems(r.data.items || []);
      setTotal(r.data.total || 0);
    } catch (err) {
      toast.error(err?.response?.data?.detail || (bg ? "Грешка." : "Error."));
    }
    setLoading(false);
  }, [API, q, category, page, onlyNew, bg]);

  useEffect(() => { load(); }, [load]);

  const totalPages = Math.max(1, Math.ceil(total / 25));

  return (
    <Card className="border-2 border-blue-100 dark:border-blue-900" data-testid="usda-browser">
      <CardContent className="p-4">
        <div className="flex items-center gap-2 mb-3">
          <Database className="w-4 h-4 text-blue-600" />
          <h3 className="font-bold text-sm">{bg ? "USDA (23,600 записа)" : "USDA (23,600 records)"}</h3>
          <span className="text-[10px] text-muted-foreground ml-auto">
            {loading ? "…" : bg ? `${total} налични` : `${total} available`}
          </span>
          {onManualAdd && (
            <Button
              size="sm"
              variant="outline"
              className="h-7 text-[10px] border-purple-300 text-purple-700 hover:bg-purple-50"
              onClick={onManualAdd}
              data-testid="nutrami-manual-add-btn"
              title={bg ? "Добави храна извън USDA (вносни, локални и др.)" : "Add a non-USDA food (imports, local, etc.)"}
            >
              <Plus className="w-3 h-3 mr-1" /> {bg ? "Ръчно" : "Manual"}
            </Button>
          )}
          {onBulkCsv && (
            <Button
              size="sm"
              variant="outline"
              className="h-7 text-[10px] border-orange-300 text-orange-700 hover:bg-orange-50"
              onClick={onBulkCsv}
              data-testid="nutrami-bulk-csv-btn"
              title={bg ? "Bulk импорт от CSV (100+ храни наведнъж)" : "Bulk import from CSV (100+ foods at once)"}
            >
              <Database className="w-3 h-3 mr-1" /> {bg ? "CSV" : "CSV"}
            </Button>
          )}
        </div>

        <div className="space-y-2 mb-3">
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder={bg ? "Търсене (напр. tuna, apple, chicken)…" : "Search (e.g. tuna, apple, chicken)…"}
              value={q}
              onChange={e => { setQ(e.target.value); setPage(1); }}
              className="pl-8 h-9 text-xs"
              data-testid="usda-search"
            />
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Input
              placeholder={bg ? "USDA категория (по избор)" : "USDA category (optional)"}
              value={category}
              onChange={e => { setCategory(e.target.value); setPage(1); }}
              className="h-8 text-xs flex-1 min-w-[160px]"
              data-testid="usda-category-filter"
            />
            <label className="flex items-center gap-1.5 text-[11px] cursor-pointer">
              <input
                type="checkbox"
                checked={onlyNew}
                onChange={e => setOnlyNew(e.target.checked)}
                data-testid="usda-only-new"
              />
              {bg ? "Само нови" : "Only new"}
            </label>
            <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="usda-refresh">
              {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
            </Button>
          </div>
        </div>

        <div className="max-h-[520px] overflow-y-auto space-y-1.5" data-testid="usda-list">
          {items.length === 0 && !loading && (
            <p className="text-xs text-center text-muted-foreground py-8">
              {bg ? "Няма резултати." : "No results."}
            </p>
          )}
          {items.map(row => (
            <div
              key={row.fdc_id}
              className={`flex items-start justify-between gap-2 p-2 rounded-lg border ${
                row.already_in_nutrami
                  ? "bg-emerald-50 border-emerald-200 dark:bg-emerald-950/30"
                  : "bg-white dark:bg-gray-900 hover:border-blue-300 border-gray-200"
              }`}
              data-testid={`usda-row-${row.fdc_id}`}
            >
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold truncate">{row.name}</p>
                <div className="flex items-center gap-2 mt-0.5 text-[10px] text-muted-foreground">
                  <span>fdc={row.fdc_id}</span>
                  {row.category && <span>· {row.category}</span>}
                  <span>· {row.calories}kcal</span>
                  <span>· P{row.protein} C{row.carbs} F{row.fat}</span>
                </div>
                <div className="flex items-center gap-1 mt-1">
                  <UsdaPreviewBadge macros={row} />
                </div>
              </div>
              {row.already_in_nutrami ? (
                <span className="text-[10px] text-emerald-700 font-semibold whitespace-nowrap flex items-center gap-1">
                  <Check className="w-3 h-3" /> {bg ? "Добавен" : "Added"}
                </span>
              ) : (
                <Button
                  size="sm"
                  className="h-7 px-2 gradient-primary text-white text-[11px]"
                  onClick={() => onImport(row)}
                  data-testid={`usda-import-${row.fdc_id}`}
                >
                  <Plus className="w-3 h-3 mr-1" /> {bg ? "Добави" : "Add"}
                </Button>
              )}
            </div>
          ))}
        </div>

        <div className="flex items-center justify-between mt-3 pt-2 border-t">
          <Button
            variant="outline" size="sm" disabled={page <= 1}
            onClick={() => setPage(p => p - 1)}
            data-testid="usda-page-prev"
          >
            <ChevronLeft className="w-3 h-3" />
          </Button>
          <span className="text-xs text-muted-foreground">
            {bg ? "Страница" : "Page"} {page} / {totalPages}
          </span>
          <Button
            variant="outline" size="sm" disabled={page >= totalPages}
            onClick={() => setPage(p => p + 1)}
            data-testid="usda-page-next"
          >
            <ChevronRight className="w-3 h-3" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Nutrami List (right column)
// ─────────────────────────────────────────────────────────────────────────
function NutramiList({ API, bg, categories, tags, onChanged, onEdit }) {
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("");
  const [tag, setTag] = useState("");
  const [coverage, setCoverage] = useState("");
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/nutrami/list`, {
        params: { q, category, tag, coverage, page, limit: 25 },
      });
      setItems(r.data.items || []);
      setTotal(r.data.total || 0);
    } catch (err) {
      toast.error(err?.response?.data?.detail || (bg ? "Грешка." : "Error."));
    }
    setLoading(false);
  }, [API, q, category, tag, coverage, page, bg]);

  useEffect(() => { load(); }, [load]);

  const remove = async (id) => {
    if (!window.confirm(bg ? "Изтриване от Nutrami?" : "Remove from Nutrami?")) return;
    try {
      await axios.delete(`${API}/admin/nutrami/${id}`);
      toast.success(bg ? "Изтрито." : "Removed.");
      load(); onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || (bg ? "Грешка." : "Error."));
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / 25));

  return (
    <Card className="border-2 border-emerald-100 dark:border-emerald-900" data-testid="nutrami-list">
      <CardContent className="p-4">
        <div className="flex items-center gap-2 mb-3">
          <Sparkles className="w-4 h-4 text-emerald-600" />
          <h3 className="font-bold text-sm">Nutrami DB ({total})</h3>
          <span className="text-[10px] text-muted-foreground ml-auto">
            {loading ? "…" : ""}
          </span>
        </div>

        <div className="space-y-2 mb-3">
          <Input
            placeholder={bg ? "Търсене в Nutrami…" : "Search Nutrami…"}
            value={q}
            onChange={e => { setQ(e.target.value); setPage(1); }}
            className="h-8 text-xs"
            data-testid="nutrami-search"
          />
          <div className="flex items-center gap-2 flex-wrap">
            <select
              value={category}
              onChange={e => { setCategory(e.target.value); setPage(1); }}
              className="h-8 rounded-md border text-xs px-2 flex-1 bg-white dark:bg-gray-900"
              data-testid="nutrami-cat-filter"
            >
              <option value="">{bg ? "Всички категории" : "All categories"}</option>
              {categories.map(c => (
                <option key={c.key} value={c.key}>{bg ? c.label_bg : c.label_en}</option>
              ))}
            </select>
            <select
              value={tag}
              onChange={e => { setTag(e.target.value); setPage(1); }}
              className="h-8 rounded-md border text-xs px-2 flex-1 bg-white dark:bg-gray-900"
              data-testid="nutrami-tag-filter"
            >
              <option value="">{bg ? "Всички тагове" : "All tags"}</option>
              {tags.map(t => (
                <option key={t.key} value={t.key}>{bg ? t.label_bg : t.label_en}</option>
              ))}
            </select>
          </div>
          <select
            value={coverage}
            onChange={e => { setCoverage(e.target.value); setPage(1); }}
            className="h-8 rounded-md border text-xs px-2 w-full bg-white dark:bg-gray-900"
            data-testid="nutrami-coverage-filter"
          >
            <option value="">{bg ? "🧬 Всякакво покритие на пътища" : "🧬 Any pathway coverage"}</option>
            <option value="zero">{bg ? "❌ Без пътища (0)" : "❌ No pathways (0)"}</option>
            <option value="poor">{bg ? "⚠️ Слабо покритие (< 5 пътища)" : "⚠️ Poor coverage (< 5 pathways)"}</option>
            <option value="vault3">{bg ? "✨ ≥ 3 vault-релевантни" : "✨ ≥ 3 vault-relevant"}</option>
          </select>
        </div>

        <div className="max-h-[520px] overflow-y-auto space-y-1.5" data-testid="nutrami-rows">
          {items.length === 0 && !loading && (
            <p className="text-xs text-center text-muted-foreground py-8">
              {bg ? "Все още няма храни. Добавете от USDA левия панел." : "No foods yet. Add some from the USDA browser."}
            </p>
          )}
          {items.map(row => (
            <div
              key={row.id}
              className="flex items-start justify-between gap-2 p-2 rounded-lg border bg-white dark:bg-gray-900"
              data-testid={`nutrami-row-${row.id}`}
            >
              <div className="flex-1 min-w-0">
                <p className="text-xs font-bold truncate">{row.name_bg}</p>
                <p className="text-[10px] text-muted-foreground truncate">{row.name_en}</p>
                <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                  <span className="px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800 text-[9px] font-semibold">
                    {categories.find(c => c.key === row.primary_category)?.[bg ? "label_bg" : "label_en"] || row.primary_category}
                  </span>
                  {(row.tags || []).slice(0, 3).map(t => (
                    <span key={t} className="px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 text-[9px]">
                      {tags.find(x => x.key === t)?.[bg ? "label_bg" : "label_en"] || t}
                    </span>
                  ))}
                  <PathwaysBadge
                    API={API}
                    bg={bg}
                    row={row}
                    onRetagged={load}
                  />
                </div>
                <div className="grid grid-cols-4 gap-1 mt-1.5 text-[9px]">
                  <MacroPill label="kcal" value={row.calories} />
                  <MacroPill label="P" value={row.protein} unit="g" />
                  <MacroPill label="C" value={row.carbs} unit="g" />
                  <MacroPill label="F" value={row.fat} unit="g" />
                  <NisPill nis={row.nis_percent} tier={row.nis_tier} color={row.nis_tier_color} />
                  <MacroPill label="DIL" value={row.dil_g} unit="g" />
                  <MacroPill label="Infl" value={row.inflammation_index_pred} />
                  <MacroPill label="fiber" value={row.fiber} unit="g" />
                </div>
                {/* iter958 — MDI (nutrient density) + SI (satiety) pills */}
                <div className="flex items-center gap-1.5 mt-1 text-[9px]">
                  <MdiPill
                    value={row.mdi_percent}
                    tier={row.mdi_tier}
                    color={row.mdi_tier_color}
                    contributors={row.contributors}
                    bg={bg}
                  />
                  <SatietyPill
                    value={row.satiety_index}
                    tier={row.satiety_tier}
                    color={row.satiety_color}
                    bg={bg}
                  />
                </div>
              </div>
              <div className="flex flex-col gap-1">
                <Button
                  variant="outline" size="sm"
                  className="h-7 w-7 p-0 border-blue-200 text-blue-600 hover:bg-blue-50"
                  onClick={() => onEdit?.(row)}
                  data-testid={`nutrami-edit-${row.id}`}
                  title={bg ? "Редактирай" : "Edit"}
                >
                  <Pencil className="w-3 h-3" />
                </Button>
                <Button
                  variant="outline" size="sm"
                  className="h-7 w-7 p-0 border-red-200 text-red-600 hover:bg-red-50"
                  onClick={() => remove(row.id)}
                  data-testid={`nutrami-delete-${row.id}`}
                  title={bg ? "Изтрий" : "Delete"}
                >
                  <Trash2 className="w-3 h-3" />
                </Button>
              </div>
            </div>
          ))}
        </div>

        <div className="flex items-center justify-between mt-3 pt-2 border-t">
          <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>
            <ChevronLeft className="w-3 h-3" />
          </Button>
          <span className="text-xs text-muted-foreground">{bg ? "Страница" : "Page"} {page} / {totalPages}</span>
          <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>
            <ChevronRight className="w-3 h-3" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function MacroPill({ label, value, unit = "" }) {
  return (
    <div className="flex items-center justify-between px-1.5 py-0.5 rounded bg-gray-50 dark:bg-gray-800">
      <span className="text-gray-500 dark:text-gray-400">{label}</span>
      <span className="font-semibold tabular-nums">
        {value != null && !Number.isNaN(Number(value)) ? `${Number(value).toFixed(1)}${unit}` : "—"}
      </span>
    </div>
  );
}

/**
 * NIS pill — coloured per official NIS v1.1.9 tier palette
 * (tier_color returned by nutramilo_nis package: Low #10B981, Moderate #34D399,
 * Medium #F59E0B, High #F97316, Very high #EF4444).
 */
function NisPill({ nis, tier, color }) {
  const bg = color || (
    tier === "High" || tier === "Very high" ? "#F97316"
      : tier === "Medium" ? "#F59E0B"
      : tier === "Moderate" ? "#34D399"
      : tier === "Low" ? "#10B981"
      : "#9CA3AF"
  );
  return (
    <div
      className="flex items-center justify-between px-1.5 py-0.5 rounded text-white font-bold"
      style={{ background: bg }}
      title={`NIS v1.1.9 · ${tier || "—"}`}
    >
      <span className="opacity-90">NIS</span>
      <span className="tabular-nums">
        {nis != null && !Number.isNaN(Number(nis)) ? `${Number(nis).toFixed(0)}%` : "—"}
      </span>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// MdiPill — iter958. Micronutrient density (0-100, per 100 kcal).
// Popover reveals the top-8 nutrient contributors (name + %RDA).
// ─────────────────────────────────────────────────────────────────────────
function MdiPill({ value, tier, color, contributors, bg: bgLang }) {
  if (value == null || Number.isNaN(Number(value))) {
    return (
      <div className="flex items-center gap-1 px-1.5 py-0.5 rounded bg-gray-100 text-gray-500 border border-gray-200">
        <span>MDI</span><span>—</span>
      </div>
    );
  }
  const dotColor = color || (
    value >= 60 ? "#10B981" : value >= 30 ? "#F59E0B" : "#9CA3AF"
  );
  const tierLabel = tier || (value >= 60 ? "Nutrient-dense" : value >= 30 ? "Moderate" : "Sparse");
  const tierLabelBg = { "Nutrient-dense": "Плътна", "Moderate": "Умерена", "Sparse": "Рядка" }[tierLabel] || tierLabel;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="flex items-center gap-1 px-1.5 py-0.5 rounded border border-gray-200 bg-white dark:bg-gray-900 hover:brightness-95 transition"
          title={bgLang ? `MDI (плътност на микронутриенти)` : `MDI (micronutrient density)`}
          data-testid="nutrami-mdi-pill"
        >
          <span
            className="w-1.5 h-1.5 rounded-full shrink-0"
            style={{ background: dotColor }}
          />
          <span className="text-gray-500">MDI</span>
          <span className="font-bold tabular-nums">{Number(value).toFixed(0)}</span>
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-64 p-3" data-testid="nutrami-mdi-popover">
        <div className="flex items-center gap-2 pb-2 mb-2 border-b">
          <div className="w-3 h-3 rounded-full shrink-0" style={{ background: dotColor }} />
          <div className="min-w-0">
            <div className="text-xs font-bold">MDI · {Number(value).toFixed(0)}/100</div>
            <div className="text-[10px] text-muted-foreground">{bgLang ? tierLabelBg : tierLabel}</div>
          </div>
        </div>
        {contributors && contributors.length > 0 ? (
          <div className="space-y-1 max-h-52 overflow-y-auto">
            <p className="text-[10px] font-semibold text-muted-foreground">
              {bgLang ? "Топ приноси (% от RDA):" : "Top contributors (% RDA):"}
            </p>
            {contributors.slice(0, 8).map((c, i) => (
              <div key={i} className="flex items-center gap-2 text-[10.5px]">
                <span className="flex-1 truncate">{bgLang ? c.bg : c.key}</span>
                <span className="tabular-nums font-semibold text-emerald-700">
                  {Number(c.pct_rda).toFixed(0)}%
                </span>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-[10.5px] text-muted-foreground">
            {bgLang ? "Няма достатъчно микро данни." : "Not enough micro data."}
          </p>
        )}
        <p className="text-[9px] text-muted-foreground mt-2 pt-2 border-t italic">
          {bgLang
            ? "0-100 · % RDA сумирано на 100 kcal, capped 50% на нутриент."
            : "0-100 · sum of %RDA per 100 kcal, capped at 50% per nutrient."}
        </p>
      </PopoverContent>
    </Popover>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// SatietyPill — iter958. Fullness per 100g (20-250, 100 ≈ white bread).
// ─────────────────────────────────────────────────────────────────────────
function SatietyPill({ value, tier, color, bg: bgLang }) {
  if (value == null || Number.isNaN(Number(value))) {
    return (
      <div className="flex items-center gap-1 px-1.5 py-0.5 rounded bg-gray-100 text-gray-500 border border-gray-200">
        <span>SI</span><span>—</span>
      </div>
    );
  }
  const dotColor = color || (
    value >= 130 ? "#10B981" : value >= 80 ? "#F59E0B" : "#EF4444"
  );
  const tierLabel = tier || (value >= 130 ? "Very filling" : value >= 80 ? "Moderate" : "Weak satiety");
  const tierLabelBg = { "Very filling": "Много засищаща", "Moderate": "Умерена", "Weak satiety": "Слаба" }[tierLabel] || tierLabel;

  return (
    <div
      className="flex items-center gap-1 px-1.5 py-0.5 rounded border border-gray-200 bg-white dark:bg-gray-900"
      title={bgLang ? `SI · ${tierLabelBg}` : `SI · ${tierLabel}`}
      data-testid="nutrami-si-pill"
    >
      <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: dotColor }} />
      <span className="text-gray-500">SI</span>
      <span className="font-bold tabular-nums">{Number(value).toFixed(0)}</span>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// PathwaysBadge — iter953 (rewritten iter957)
// Compact "🧬 N пътища · V vault" badge whose colour reflects the food's
// NET clinical impact across its tagged pathways (green = net beneficial,
// gray = neutral / no data, red = net harmful), NOT how many pathways
// have been tagged. This fixes the misleading "broccoli looks bad because
// it only has 4 pathways" issue.
// Click → popover with the food's pathway list + per-pathway valence
// pill (beneficial / harmful / neutral) + a "Re-tag" button.
// ─────────────────────────────────────────────────────────────────────────
function PathwaysBadge({ API, bg, row, onRetagged }) {
  const [retagging, setRetagging] = useState(false);
  const count = Number(row?.pathway_count ?? (row?.pathway_ids?.length || 0));
  const vaultCount = Number(row?.vault_relevant_count ?? 0);
  const ben  = Number(row?.pathway_beneficial_count ?? 0);
  const harm = Number(row?.pathway_harmful_count ?? 0);
  const overall = row?.pathway_overall_valence || "neutral";

  // Colour reflects overall clinical valence — NOT pathway count.
  const palette = count === 0
    ? { bgCls: "bg-gray-100 text-gray-500 border-gray-200" }
    : overall === "beneficial"
      ? { bgCls: "bg-emerald-50 text-emerald-700 border-emerald-200" }
      : overall === "harmful"
        ? { bgCls: "bg-red-50 text-red-700 border-red-200" }
        : { bgCls: "bg-gray-50 text-gray-700 border-gray-200" };

  const summary = count === 0
    ? (bg ? "0 пътища" : "0 pathways")
    : ben > 0 && harm > 0
      ? (bg ? `${count} пътища · ${ben}↑ / ${harm}↓` : `${count} pathways · ${ben}↑ / ${harm}↓`)
      : ben > 0
        ? (bg ? `${count} пътища · ${ben} полезни` : `${count} pathways · ${ben} good`)
        : harm > 0
          ? (bg ? `${count} пътища · ${harm} вредни` : `${count} pathways · ${harm} harmful`)
          : (bg ? `${count} пътища · неутрални` : `${count} pathways · neutral`);

  const retag = async () => {
    setRetagging(true);
    try {
      const r = await axios.post(`${API}/admin/nutrami/${row.id}/compute-pathways`);
      toast.success(
        bg
          ? `Ретагнато: ${r.data.pathway_count} пътища (${r.data.matched_via})`
          : `Re-tagged: ${r.data.pathway_count} pathways (${r.data.matched_via})`
      );
      onRetagged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || (bg ? "Грешка при ретагване." : "Re-tag failed."));
    } finally {
      setRetagging(false);
    }
  };

  const impact = row?.pathway_impact || [];

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={`px-1.5 py-0.5 rounded border text-[9px] font-semibold flex items-center gap-1 hover:brightness-95 transition ${palette.bgCls}`}
          data-testid={`nutrami-pathways-badge-${row.id}`}
          title={bg ? "Клик за подробности" : "Click for details"}
        >
          <Dna className="w-2.5 h-2.5" />
          {summary}
          <span className="opacity-70">·</span>
          <span title={bg ? "vault-релевантни" : "vault-relevant"}>
            {vaultCount} vault
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-96 p-3"
        data-testid={`nutrami-pathways-popover-${row.id}`}
      >
        <div className="flex items-center justify-between gap-2 mb-2 pb-2 border-b">
          <div className="min-w-0">
            <div className="text-xs font-bold flex items-center gap-1">
              <Dna className="w-3.5 h-3.5 text-emerald-600" />
              {bg ? "KEGG пътища" : "KEGG pathways"}
            </div>
            <div className="text-[10px] text-muted-foreground truncate">{row.name_bg}</div>
            <div className="text-[10px] mt-0.5 flex items-center gap-1.5 flex-wrap">
              <span className="font-semibold">{count}</span>
              {bg ? "общо" : "total"}
              {ben > 0 && (
                <span className="px-1 py-0.5 rounded bg-emerald-100 text-emerald-800 font-bold">
                  {ben} {bg ? "полезни" : "beneficial"}
                </span>
              )}
              {harm > 0 && (
                <span className="px-1 py-0.5 rounded bg-red-100 text-red-800 font-bold">
                  {harm} {bg ? "вредни" : "harmful"}
                </span>
              )}
              {vaultCount > 0 && (
                <span className="px-1 py-0.5 rounded bg-blue-100 text-blue-800 font-bold">
                  {vaultCount} vault
                </span>
              )}
              {row.pathway_matched_via && (
                <span className="ml-auto text-muted-foreground">
                  {bg ? "източник" : "via"}: {row.pathway_matched_via}
                </span>
              )}
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            className="h-7 text-[10px] shrink-0"
            onClick={retag}
            disabled={retagging}
            data-testid={`nutrami-retag-btn-${row.id}`}
          >
            {retagging
              ? <Loader2 className="w-3 h-3 animate-spin" />
              : <RefreshCw className="w-3 h-3 mr-1" />}
            {bg ? "Ретагни" : "Re-tag"}
          </Button>
        </div>

        <div className="max-h-64 overflow-y-auto space-y-1">
          {impact.length === 0 && (
            <p className="text-[11px] text-muted-foreground text-center py-3">
              {bg
                ? 'Няма записани пътища. Натисни „Ретагни" за да ги изчислиш.'
                : 'No pathways stored. Click "Re-tag" to compute.'}
            </p>
          )}
          {impact.map((p, idx) => {
            const dir = p.direction || "";
            const val = p.clinical_valence || "neutral";
            const dirIcon = dir === "activate" || dir.includes("up")
              ? <ArrowUpRight className="w-3 h-3" />
              : dir === "suppress" || dir === "inhibit" || dir.includes("down")
                ? <ArrowDownRight className="w-3 h-3" />
                : <Minus className="w-3 h-3" />;

            const valStyle = val === "beneficial"
              ? { row: "bg-emerald-50 dark:bg-emerald-950/30 border-emerald-200",
                  pill: "bg-emerald-600 text-white",
                  pillLabel: bg ? "полезно" : "beneficial",
                  arrowCls: "text-emerald-600" }
              : val === "harmful"
                ? { row: "bg-red-50 dark:bg-red-950/30 border-red-200",
                    pill: "bg-red-600 text-white",
                    pillLabel: bg ? "вредно" : "harmful",
                    arrowCls: "text-red-600" }
                : { row: "bg-gray-50 dark:bg-gray-800 border-gray-200",
                    pill: "bg-gray-400 text-white",
                    pillLabel: bg ? "неутрално" : "neutral",
                    arrowCls: "text-gray-400" };

            return (
              <div
                key={`${p.pathway_id}-${idx}`}
                className={`flex items-center gap-1.5 text-[10.5px] px-1.5 py-1 rounded border ${valStyle.row}`}
                data-testid={`nutrami-pw-item-${p.pathway_id}`}
              >
                <span className={valStyle.arrowCls}>{dirIcon}</span>
                <span className="truncate flex-1">{p.name_bg || p.pathway_id}</span>
                <span
                  className={`shrink-0 px-1 py-0.5 rounded text-[8px] font-bold uppercase tracking-wider ${valStyle.pill}`}
                  data-testid={`nutrami-pw-valence-${p.pathway_id}`}
                >
                  {valStyle.pillLabel}
                </span>
              </div>
            );
          })}
        </div>
        <p className="text-[9px] text-muted-foreground mt-2 pt-2 border-t italic">
          {bg
            ? "* Клиничната оценка е обща (за здрав възрастен) и не заменя индивидуален съвет."
            : "* Clinical labels reflect a general adult profile — not personalised advice."}
        </p>
      </PopoverContent>
    </Popover>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Add-from-USDA Modal
// ─────────────────────────────────────────────────────────────────────────
function AddFromUSDAModal({ API, bg, categories, tags, usdaRow, onClose, onSuccess }) {
  const [nameBg, setNameBg] = useState(usdaRow?._prefill_name_bg || "");
  const [category, setCategory] = useState("");
  const [selectedTags, setSelectedTags] = useState([]);
  const [state, setState] = useState("raw");
  const [portion, setPortion] = useState(100);
  const [aliases, setAliases] = useState("");
  const [saving, setSaving] = useState(false);
  const dupState = useNameDuplicateCheck(API, nameBg, category);

  // Auto-guess category from USDA foodCategory
  useEffect(() => {
    const usdaCat = (usdaRow?.category || "").toLowerCase();
    const guess = categories.find(c => usdaCat.includes(c.key.replace("_", " "))) ||
      categories.find(c => usdaCat.includes(c.label_en.toLowerCase().split(" ")[0]));
    if (guess) setCategory(guess.key);
  }, [usdaRow, categories]);

  const toggleTag = (key) => {
    setSelectedTags(prev => prev.includes(key) ? prev.filter(t => t !== key) : [...prev, key]);
  };

  const submit = async () => {
    if (!nameBg.trim()) { toast.error(bg ? "Задължително българско име." : "Bulgarian name required."); return; }
    if (!category) { toast.error(bg ? "Изберете категория." : "Pick a category."); return; }
    setSaving(true);
    try {
      const r = await axios.post(`${API}/admin/nutrami/add-from-usda`, {
        fdc_id: usdaRow.fdc_id,
        name_bg: nameBg.trim(),
        primary_category: category,
        tags: selectedTags,
        state,
        portion_g: Number(portion) || 100,
        aliases: aliases.split(",").map(a => a.trim()).filter(Boolean),
      });
      const pw = r?.data?.pathways;
      const pwCount = pw?.pathway_count || 0;
      const pwVia   = pw?.matched_via || "";
      if (pwCount > 0) {
        toast.success(
          bg
            ? `✅ Импортирано + ${pwCount} KEGG пътища тагнати (${pwVia})`
            : `✅ Imported + ${pwCount} KEGG pathways tagged (${pwVia})`
        );
      } else {
        toast.success(
          bg
            ? 'Импортирано (0 пътища — натисни „Ретагни" за да опитаме отново)'
            : 'Imported (0 pathways — click "Re-tag" to retry)'
        );
      }
      onSuccess?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || (bg ? "Грешка." : "Error."));
    }
    setSaving(false);
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="nutrami-add-modal"
    >
      <Card className="w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <CardContent className="p-5 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-bold flex items-center gap-2">
              <Plus className="w-4 h-4 text-emerald-600" />
              {bg ? "Добавяне в Nutrami" : "Add to Nutrami"}
            </h3>
            <Button variant="ghost" size="sm" onClick={onClose}>
              <X className="w-4 h-4" />
            </Button>
          </div>

          <div className="p-3 bg-blue-50 dark:bg-blue-950/30 rounded-lg border border-blue-200 dark:border-blue-800">
            <p className="text-[11px] text-blue-800 dark:text-blue-200 font-semibold">
              USDA: {usdaRow.name}
            </p>
            <p className="text-[10px] text-blue-700 mt-0.5">
              fdc={usdaRow.fdc_id} · {usdaRow.calories} kcal · P{usdaRow.protein} C{usdaRow.carbs} F{usdaRow.fat}
            </p>
          </div>

          <div>
            <label className="text-[11px] font-semibold">{bg ? "Българско име *" : "Bulgarian name *"}</label>
            <Input
              value={nameBg} onChange={e => setNameBg(e.target.value)}
              placeholder={bg ? "напр. Риба тон прясна" : "e.g. Tuna, fresh, raw"}
              className="h-9 text-xs" data-testid="modal-name-bg"
            />
            <DuplicateWarning bg={bg} dupState={dupState} />
          </div>

          <div>
            <label className="text-[11px] font-semibold">{bg ? "Категория *" : "Category *"}</label>
            <select
              value={category} onChange={e => setCategory(e.target.value)}
              className="w-full h-9 rounded-md border text-xs px-2 bg-white dark:bg-gray-900"
              data-testid="modal-category"
            >
              <option value="">{bg ? "— избери —" : "— pick —"}</option>
              {categories.map(c => (
                <option key={c.key} value={c.key}>{bg ? c.label_bg : c.label_en}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-[11px] font-semibold flex items-center gap-1">
              <Tag className="w-3 h-3" /> {bg ? "Тагове (диетични)" : "Tags (dietary)"}
            </label>
            <div className="flex flex-wrap gap-1.5 mt-1.5">
              {tags.map(t => (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => toggleTag(t.key)}
                  data-testid={`modal-tag-${t.key}`}
                  className={`px-2 py-1 rounded-full text-[10px] font-semibold border transition-colors ${
                    selectedTags.includes(t.key)
                      ? "bg-emerald-600 text-white border-emerald-600"
                      : "bg-white text-gray-700 border-gray-300 hover:border-emerald-400"
                  }`}
                >
                  {bg ? t.label_bg : t.label_en}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[11px] font-semibold">{bg ? "Състояние" : "State"}</label>
              <select
                value={state} onChange={e => setState(e.target.value)}
                className="w-full h-9 rounded-md border text-xs px-2 bg-white dark:bg-gray-900"
                data-testid="modal-state"
              >
                <option value="raw">{bg ? "Сурово" : "Raw"}</option>
                <option value="cooked">{bg ? "Готвено" : "Cooked"}</option>
                <option value="fermented">{bg ? "Ферментирало" : "Fermented"}</option>
                <option value="dried">{bg ? "Изсушено" : "Dried"}</option>
                <option value="pickled">{bg ? "Мариновано" : "Pickled"}</option>
                <option value="canned">{bg ? "Консервирано" : "Canned"}</option>
              </select>
            </div>
            <div>
              <label className="text-[11px] font-semibold">{bg ? "Порция (g)" : "Portion (g)"}</label>
              <Input
                type="number" min="1" max="2000" value={portion}
                onChange={e => setPortion(e.target.value)}
                className="h-9 text-xs" data-testid="modal-portion"
              />
            </div>
          </div>

          <div>
            <label className="text-[11px] font-semibold">{bg ? "Синоними (запетая)" : "Aliases (comma-separated)"}</label>
            <Input
              value={aliases} onChange={e => setAliases(e.target.value)}
              placeholder={bg ? "напр. тон, тона, tuna" : "e.g. tuna, тон, тона"}
              className="h-9 text-xs" data-testid="modal-aliases"
            />
          </div>

          <div className="flex gap-2 pt-2">
            <Button variant="outline" onClick={onClose} className="flex-1" data-testid="modal-cancel">
              {bg ? "Отказ" : "Cancel"}
            </Button>
            <Button
              onClick={submit} disabled={saving}
              className="flex-1 gradient-primary text-white"
              data-testid="modal-submit"
            >
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Check className="w-3.5 h-3.5 mr-1.5" />}
              {bg ? "Добави в Nutrami" : "Add to Nutrami"}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Edit Nutrami Modal — full edit of an existing curated food.
//
// Editable fields:
//   name_bg, primary_category, tags, state, portion_g, aliases, notes
//   AND per-100g macros: calories, protein, carbs, fat, fiber, sugar, sat_fat
//
// "Auto-tag" button re-runs `suggest_tags` on the backend by sending
// `apply_auto_tags=true` — the backend unions rule-derived tags with the
// user-picked ones so nothing already selected is lost.
// ─────────────────────────────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────────────────
// ManualAddModal — iter955
// Admin-driven manual food entry (non-USDA). Full macro panel required,
// micros optional. Multipart POST with the food JSON and an optional
// label image (< 5MB, PNG/JPG/WEBP).
// ─────────────────────────────────────────────────────────────────────────
const MANUAL_SOURCE_OPTIONS = [
  { key: "label",             label_bg: "📸 Опаковка / етикет",   label_en: "📸 Package label" },
  { key: "manufacturer_site", label_bg: "🌐 Сайт на производител", label_en: "🌐 Manufacturer site" },
  { key: "textbook",          label_bg: "📚 Учебник / рецензирано",label_en: "📚 Textbook / peer-reviewed" },
  { key: "bulk_supplier",     label_bg: "📦 Bulk доставчик",       label_en: "📦 Bulk supplier" },
  { key: "restaurant",        label_bg: "🍽 Ресторант",             label_en: "🍽 Restaurant" },
  { key: "other",             label_bg: "Друго",                   label_en: "Other" },
];

const MANUAL_REQUIRED_MACROS = [
  { key: "calories",      label: "kcal",   unit: "kcal" },
  { key: "protein",       label: "P",      unit: "g" },
  { key: "carbs",         label: "C",      unit: "g" },
  { key: "fat",           label: "F",      unit: "g" },
  { key: "fiber",         label: "Fiber",  unit: "g" },
  { key: "sugar",         label: "Sugar",  unit: "g" },
  { key: "saturated_fat", label: "Sat fat", unit: "g" },
];

const MANUAL_OPTIONAL_MICROS = [
  { key: "sodium_mg",       label: "Na",    unit: "mg" },
  { key: "potassium_mg",    label: "K",     unit: "mg" },
  { key: "calcium_mg",      label: "Ca",    unit: "mg" },
  { key: "magnesium_mg",    label: "Mg",    unit: "mg" },
  { key: "iron_mg",         label: "Fe",    unit: "mg" },
  { key: "zinc_mg",         label: "Zn",    unit: "mg" },
  { key: "copper_mg",       label: "Cu",    unit: "mg" },
  { key: "phosphorus_mg",   label: "P₂",    unit: "mg" },
  { key: "selenium_ug",     label: "Se",    unit: "µg" },
  { key: "manganese_mg",    label: "Mn",    unit: "mg" },
  { key: "vit_a_ug",        label: "A",     unit: "µg" },
  { key: "vit_c_mg",        label: "C",     unit: "mg" },
  { key: "vit_d_ug",        label: "D",     unit: "µg" },
  { key: "vit_e_mg",        label: "E",     unit: "mg" },
  { key: "vit_k_ug",        label: "K",     unit: "µg" },
  { key: "vit_b1_mg",       label: "B1",    unit: "mg" },
  { key: "vit_b2_mg",       label: "B2",    unit: "mg" },
  { key: "vit_b3_mg",       label: "B3",    unit: "mg" },
  { key: "vit_b6_mg",       label: "B6",    unit: "mg" },
  { key: "vit_b12_ug",      label: "B12",   unit: "µg" },
  { key: "folate_ug",       label: "Folate", unit: "µg" },
  { key: "omega3_total_g",  label: "ω-3",   unit: "g" },
  { key: "omega6_la_g",     label: "ω-6",   unit: "g" },
  { key: "mufa_g",          label: "MUFA",  unit: "g" },
  { key: "pufa_g",          label: "PUFA",  unit: "g" },
];

function ManualAddModal({ API, bg, categories, tags, onClose, onSuccess }) {
  const [nameBg, setNameBg]     = useState("");
  const [nameEn, setNameEn]     = useState("");
  const [category, setCategory] = useState("");
  const [selectedTags, setSelectedTags] = useState([]);
  const [state, setState]       = useState("raw");
  const [portion, setPortion]   = useState(100);
  const [aliases, setAliases]   = useState("");
  const [notes, setNotes]       = useState("");
  const [sourceType, setSourceType]     = useState("label");
  const [sourceDetail, setSourceDetail] = useState("");
  const [macros, setMacros]     = useState({});
  const [labelFile, setLabelFile] = useState(null);
  const [labelPreview, setLabelPreview] = useState(null);
  const [saving, setSaving]     = useState(false);
  const dupState = useNameDuplicateCheck(API, nameBg, category);

  const updateMacro = (key, val) => {
    setMacros(prev => ({ ...prev, [key]: val }));
  };

  const onLabelPick = (e) => {
    const f = e.target.files?.[0];
    if (!f) { setLabelFile(null); setLabelPreview(null); return; }
    if (f.size > 5 * 1024 * 1024) {
      toast.error(bg ? "Файлът е твърде голям (>5MB)." : "File too large (>5MB).");
      return;
    }
    setLabelFile(f);
    const reader = new FileReader();
    reader.onload = ev => setLabelPreview(ev.target?.result);
    reader.readAsDataURL(f);
  };

  const toggleTag = (key) => {
    setSelectedTags(prev => prev.includes(key) ? prev.filter(t => t !== key) : [...prev, key]);
  };

  const submit = async () => {
    if (!nameBg.trim()) { toast.error(bg ? "Задължително българско име." : "Bulgarian name required."); return; }
    if (!category)      { toast.error(bg ? "Изберете категория." : "Pick a category."); return; }
    for (const m of MANUAL_REQUIRED_MACROS) {
      const v = macros[m.key];
      if (v === undefined || v === null || v === "") {
        toast.error(bg ? `Задължителна стойност: ${m.label}` : `Required: ${m.label}`);
        return;
      }
    }

    const macrosOut = {};
    [...MANUAL_REQUIRED_MACROS, ...MANUAL_OPTIONAL_MICROS].forEach(m => {
      const v = macros[m.key];
      if (v !== undefined && v !== null && v !== "") {
        const n = Number(v);
        if (!Number.isNaN(n)) macrosOut[m.key] = n;
      }
    });

    const payload = {
      name_bg:          nameBg.trim(),
      name_en:          nameEn.trim(),
      primary_category: category,
      tags:             selectedTags,
      state,
      portion_g:        Number(portion) || 100,
      aliases:          aliases.split(",").map(a => a.trim()).filter(Boolean),
      notes:            notes.trim(),
      source_type:      sourceType,
      source_detail:    sourceDetail.trim(),
      macros:           macrosOut,
    };

    const fd = new FormData();
    fd.append("data", JSON.stringify(payload));
    if (labelFile) fd.append("label_image", labelFile);

    setSaving(true);
    try {
      const r = await axios.post(`${API}/admin/nutrami/add-manual`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const pwCount = r?.data?.pathways?.pathway_count || 0;
      const pwVia   = r?.data?.pathways?.matched_via || "";
      toast.success(
        pwCount > 0
          ? (bg
              ? `Ръчно добавено + ${pwCount} KEGG пътища (${pwVia})`
              : `Manually added + ${pwCount} KEGG pathways (${pwVia})`)
          : (bg ? "Ръчно добавено (0 пътища)" : "Manually added (0 pathways)")
      );
      onSuccess?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || (bg ? "Грешка." : "Error."));
    }
    setSaving(false);
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="nutrami-manual-modal"
    >
      <Card className="w-full max-w-3xl max-h-[92vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <CardContent className="p-5 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-bold flex items-center gap-2">
              <Plus className="w-4 h-4 text-purple-600" />
              {bg ? "Ръчно добавяне (извън USDA)" : "Manual add (non-USDA)"}
            </h3>
            <Button variant="ghost" size="sm" onClick={onClose} data-testid="manual-close-btn">
              <X className="w-4 h-4" />
            </Button>
          </div>

          <div className="p-3 bg-purple-50 dark:bg-purple-950/30 rounded-lg border border-purple-200 dark:border-purple-800">
            <p className="text-[11px] text-purple-800 dark:text-purple-200">
              {bg
                ? "Използвайте това за вносни продукти, локални марки и учебникови референции. Задължителни: българско име, категория, всички макронутриенти на 100g. NIS/DIL/пътища се изчисляват автоматично."
                : "Use this for imported items, local brands, and textbook references. Required: Bulgarian name, category, and all macros per 100g. NIS/DIL/pathways compute automatically."}
            </p>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[11px] font-semibold">{bg ? "Българско име *" : "Bulgarian name *"}</label>
              <Input
                value={nameBg} onChange={e => setNameBg(e.target.value)}
                placeholder={bg ? "напр. Кокосово мляко (Aroy-D)" : "e.g. Coconut milk (Aroy-D)"}
                className="h-9 text-xs" data-testid="manual-name-bg"
              />
              <DuplicateWarning bg={bg} dupState={dupState} />
            </div>
            <div>
              <label className="text-[11px] font-semibold">{bg ? "Английско име" : "English name"}</label>
              <Input
                value={nameEn} onChange={e => setNameEn(e.target.value)}
                placeholder="e.g. Coconut milk, canned"
                className="h-9 text-xs" data-testid="manual-name-en"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[11px] font-semibold">{bg ? "Категория *" : "Category *"}</label>
              <select
                value={category} onChange={e => setCategory(e.target.value)}
                className="w-full h-9 rounded-md border text-xs px-2 bg-white dark:bg-gray-900"
                data-testid="manual-category"
              >
                <option value="">{bg ? "— избери —" : "— pick —"}</option>
                {categories.map(c => (
                  <option key={c.key} value={c.key}>{bg ? c.label_bg : c.label_en}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-[11px] font-semibold">{bg ? "Състояние" : "State"}</label>
              <select
                value={state} onChange={e => setState(e.target.value)}
                className="w-full h-9 rounded-md border text-xs px-2 bg-white dark:bg-gray-900"
                data-testid="manual-state"
              >
                <option value="raw">{bg ? "Сурово" : "Raw"}</option>
                <option value="cooked">{bg ? "Готвено" : "Cooked"}</option>
                <option value="fermented">{bg ? "Ферментирало" : "Fermented"}</option>
                <option value="dried">{bg ? "Изсушено" : "Dried"}</option>
                <option value="pickled">{bg ? "Мариновано" : "Pickled"}</option>
                <option value="canned">{bg ? "Консервирано" : "Canned"}</option>
              </select>
            </div>
          </div>

          <div className="border-t pt-3">
            <p className="text-[11px] font-bold uppercase tracking-wider text-purple-700 mb-2">
              {bg ? "Източник на данните" : "Data source"}
            </p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[11px] font-semibold">{bg ? "Тип източник *" : "Source type *"}</label>
                <select
                  value={sourceType} onChange={e => setSourceType(e.target.value)}
                  className="w-full h-9 rounded-md border text-xs px-2 bg-white dark:bg-gray-900"
                  data-testid="manual-source-type"
                >
                  {MANUAL_SOURCE_OPTIONS.map(o => (
                    <option key={o.key} value={o.key}>{bg ? o.label_bg : o.label_en}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-[11px] font-semibold">{bg ? "Детайл (по избор)" : "Detail (optional)"}</label>
                <Input
                  value={sourceDetail} onChange={e => setSourceDetail(e.target.value)}
                  placeholder={bg ? 'напр. Aroy-D 400ml — партида 2024' : 'e.g. Aroy-D 400ml batch 2024'}
                  className="h-9 text-xs" data-testid="manual-source-detail"
                />
              </div>
            </div>
            <div className="mt-2">
              <label className="text-[11px] font-semibold flex items-center gap-1.5">
                📸 {bg ? "Снимка на етикета (по избор, за одит)" : "Label photo (optional, for audit)"}
              </label>
              <input
                type="file"
                accept="image/png,image/jpeg,image/webp"
                onChange={onLabelPick}
                className="text-xs mt-1"
                data-testid="manual-label-upload"
              />
              {labelPreview && (
                <img
                  src={labelPreview}
                  alt="label preview"
                  className="mt-2 max-h-32 rounded border"
                  data-testid="manual-label-preview"
                />
              )}
            </div>
          </div>

          <div className="border-t pt-3">
            <p className="text-[11px] font-bold uppercase tracking-wider text-emerald-700 mb-2">
              {bg ? "Задължителен макро панел (на 100g)" : "Required macro panel (per 100g)"}
            </p>
            <div className="grid grid-cols-4 gap-2">
              {MANUAL_REQUIRED_MACROS.map(m => (
                <div key={m.key}>
                  <label className="text-[10px] font-semibold text-gray-600">{m.label} *</label>
                  <Input
                    type="number" step="0.1" min="0"
                    value={macros[m.key] ?? ""}
                    onChange={e => updateMacro(m.key, e.target.value)}
                    placeholder={m.unit}
                    className="h-8 text-xs"
                    data-testid={`manual-macro-${m.key}`}
                  />
                </div>
              ))}
            </div>
          </div>

          <details className="border-t pt-3 group" data-testid="manual-micros-panel">
            <summary className="cursor-pointer text-[11px] font-bold uppercase tracking-wider text-blue-700 mb-2 select-none">
              {bg ? "Минерали, витамини, мастни киселини (по избор)" : "Micronutrients (optional)"}
            </summary>
            <div className="grid grid-cols-5 gap-2 mt-2">
              {MANUAL_OPTIONAL_MICROS.map(m => (
                <div key={m.key}>
                  <label className="text-[10px] font-semibold text-gray-600">
                    {m.label} <span className="text-gray-400">({m.unit})</span>
                  </label>
                  <Input
                    type="number" step="0.01" min="0"
                    value={macros[m.key] ?? ""}
                    onChange={e => updateMacro(m.key, e.target.value)}
                    className="h-7 text-[11px]"
                    data-testid={`manual-micro-${m.key}`}
                  />
                </div>
              ))}
            </div>
          </details>

          <div className="border-t pt-3">
            <label className="text-[11px] font-semibold flex items-center gap-1">
              <Tag className="w-3 h-3" /> {bg ? "Тагове (диетични)" : "Tags (dietary)"}
            </label>
            <div className="flex flex-wrap gap-1.5 mt-1.5">
              {tags.map(t => (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => toggleTag(t.key)}
                  data-testid={`manual-tag-${t.key}`}
                  className={`px-2 py-1 rounded-full text-[10px] font-semibold border transition-colors ${
                    selectedTags.includes(t.key)
                      ? "bg-emerald-600 text-white border-emerald-600"
                      : "bg-white text-gray-700 border-gray-300 hover:border-emerald-400"
                  }`}
                >
                  {bg ? t.label_bg : t.label_en}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3 border-t pt-3">
            <div>
              <label className="text-[11px] font-semibold">{bg ? "Порция (g)" : "Portion (g)"}</label>
              <Input
                type="number" min="1" max="2000"
                value={portion} onChange={e => setPortion(e.target.value)}
                className="h-8 text-xs" data-testid="manual-portion"
              />
            </div>
            <div className="col-span-2">
              <label className="text-[11px] font-semibold">{bg ? "Синоними (запетая)" : "Aliases (comma)"}</label>
              <Input
                value={aliases} onChange={e => setAliases(e.target.value)}
                placeholder="e.g. coconut cream, кокосов крем"
                className="h-8 text-xs" data-testid="manual-aliases"
              />
            </div>
          </div>

          <div>
            <label className="text-[11px] font-semibold">{bg ? "Бележки" : "Notes"}</label>
            <Input
              value={notes} onChange={e => setNotes(e.target.value)}
              placeholder={bg ? "Всякаква допълнителна информация..." : "Any extra information..."}
              className="h-9 text-xs" data-testid="manual-notes"
            />
          </div>

          <div className="flex items-center justify-end gap-2 border-t pt-3">
            <Button variant="outline" size="sm" onClick={onClose} disabled={saving}>
              {bg ? "Отказ" : "Cancel"}
            </Button>
            <Button
              size="sm"
              onClick={submit}
              disabled={saving}
              className="bg-purple-600 hover:bg-purple-700 text-white"
              data-testid="manual-save-btn"
            >
              {saving
                ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> {bg ? "Запис..." : "Saving..."}</>
                : <><Check className="w-3 h-3 mr-1" /> {bg ? "Запази" : "Save"}</>}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}


function EditNutramiModal({ API, bg, categories, tags, record, onClose, onSuccess }) {
  const [nameBg, setNameBg]         = useState(record.name_bg || "");
  const [category, setCategory]     = useState(record.primary_category || "");
  const [selectedTags, setSelectedTags] = useState(record.tags || []);
  const [state, setState]           = useState(record.state || "raw");
  const [portion, setPortion]       = useState(record.portion_g || 100);
  const [aliases, setAliases]       = useState((record.aliases || []).join(", "));
  const [notes, setNotes]           = useState(record.notes || "");
  const [calories, setCalories]     = useState(record.calories ?? "");
  const [protein, setProtein]       = useState(record.protein ?? "");
  const [carbs, setCarbs]           = useState(record.carbs ?? "");
  const [fat, setFat]               = useState(record.fat ?? "");
  const [fiber, setFiber]           = useState(record.fiber ?? "");
  const [sugar, setSugar]           = useState(record.sugar ?? "");
  const [satFat, setSatFat]         = useState(record.saturated_fat ?? "");

  // ── iter722 — Micronutrient state (minerals + vitamins + fatty acids) ──
  const MICRO_KEYS = [
    // minerals
    "sodium_mg", "potassium_mg", "calcium_mg", "iron_mg", "magnesium_mg",
    "phosphorus_mg", "zinc_mg", "copper_mg", "manganese_mg", "selenium_ug",
    // vitamins
    "vit_a_ug", "vit_c_mg", "vit_d_ug", "vit_e_mg", "vit_k_ug",
    "vit_b1_mg", "vit_b2_mg", "vit_b3_mg", "vit_b6_mg", "vit_b12_ug", "folate_ug",
    // fatty-acid breakdown
    "omega3_total_g", "omega6_la_g", "mufa_g", "pufa_g",
  ];
  const [micros, setMicros] = useState(() => {
    const init = {};
    MICRO_KEYS.forEach(k => { init[k] = record[k] ?? ""; });
    return init;
  });
  const setMicro = (k, v) => setMicros(prev => ({ ...prev, [k]: v }));

  const [refreshing, setRefreshing] = useState(false);
  const [applyAutoTags, setApplyAutoTags] = useState(false);
  const [saving, setSaving]         = useState(false);

  const toggleTag = (key) => {
    setSelectedTags(prev => prev.includes(key) ? prev.filter(t => t !== key) : [...prev, key]);
  };

  const _num = (v) => (v === "" || v === null || v === undefined) ? null : Number(v);

  const submit = async () => {
    if (!nameBg.trim()) { toast.error(bg ? "Задължително българско име." : "Bulgarian name required."); return; }
    if (!category) { toast.error(bg ? "Изберете категория." : "Pick a category."); return; }
    setSaving(true);
    try {
      const payload = {
        name_bg: nameBg.trim(),
        primary_category: category,
        tags: selectedTags,
        state,
        portion_g: Number(portion) || 100,
        aliases: aliases.split(",").map(a => a.trim()).filter(Boolean),
        notes,
        apply_auto_tags: applyAutoTags,
      };
      // Include macro overrides only if user changed them (non-empty).
      const macros = { calories: _num(calories), protein: _num(protein), carbs: _num(carbs),
                       fat: _num(fat), fiber: _num(fiber), sugar: _num(sugar), saturated_fat: _num(satFat) };
      Object.entries(macros).forEach(([k, v]) => { if (v !== null && !Number.isNaN(v)) payload[k] = v; });

      // Include micronutrient overrides — only fields the user actually changed.
      MICRO_KEYS.forEach(k => {
        const cur = _num(micros[k]);
        const orig = record[k] ?? null;
        if (cur !== null && !Number.isNaN(cur) && cur !== orig) {
          payload[k] = cur;
        }
      });

      const r = await axios.put(`${API}/admin/nutrami/${record.id}`, payload);
      const returnedTags = r.data?.tags || selectedTags;
      const autoAdded = r.data?.auto_tags || [];
      toast.success(
        bg
          ? `Обновено. Тагове: ${returnedTags.length}${autoAdded.length ? ` (авто: ${autoAdded.length})` : ""}`
          : `Updated. Tags: ${returnedTags.length}${autoAdded.length ? ` (auto: ${autoAdded.length})` : ""}`
      );
      onSuccess?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || (bg ? "Грешка." : "Error."));
    }
    setSaving(false);
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="nutrami-edit-modal"
    >
      <Card className="w-full max-w-xl max-h-[92vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <CardContent className="p-5 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-bold flex items-center gap-2">
              <Pencil className="w-4 h-4 text-blue-600" />
              {bg ? "Редактиране: " : "Edit: "}{record.name_bg}
            </h3>
            <Button variant="ghost" size="sm" onClick={onClose}>
              <X className="w-4 h-4" />
            </Button>
          </div>

          <div className="p-3 bg-blue-50 dark:bg-blue-950/30 rounded-lg border border-blue-200 dark:border-blue-800">
            <p className="text-[10px] text-blue-700">
              {bg ? "USDA fdc" : "USDA fdc"}={record.source_fdc_id || "—"} · {record.name_en || record.name_bg}
            </p>
            {record.auto_tags?.length ? (
              <p className="text-[10px] text-blue-800 mt-1">
                <b>{bg ? "Авто-тагове:" : "Auto-tags:"}</b> {record.auto_tags.join(", ")}
              </p>
            ) : null}
          </div>

          <div>
            <label className="text-[11px] font-semibold">{bg ? "Българско име *" : "Bulgarian name *"}</label>
            <Input value={nameBg} onChange={e => setNameBg(e.target.value)}
              className="h-9 text-xs" data-testid="edit-name-bg" />
          </div>

          <div>
            <label className="text-[11px] font-semibold">{bg ? "Категория *" : "Category *"}</label>
            <select value={category} onChange={e => setCategory(e.target.value)}
              className="w-full h-9 rounded-md border text-xs px-2 bg-white dark:bg-gray-900"
              data-testid="edit-category">
              {categories.map(c => (
                <option key={c.key} value={c.key}>{bg ? c.label_bg : c.label_en}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-[11px] font-semibold flex items-center gap-1">
              <Tag className="w-3 h-3" /> {bg ? "Тагове (диетични)" : "Tags (dietary)"}
            </label>
            <div className="flex flex-wrap gap-1.5 mt-1.5">
              {tags.map(t => (
                <button
                  key={t.key} type="button" onClick={() => toggleTag(t.key)}
                  data-testid={`edit-tag-${t.key}`}
                  className={`px-2 py-1 rounded-full text-[10px] font-semibold border transition-colors ${
                    selectedTags.includes(t.key)
                      ? "bg-emerald-600 text-white border-emerald-600"
                      : "bg-white text-gray-700 border-gray-300 hover:border-emerald-400"
                  }`}
                >
                  {bg ? t.label_bg : t.label_en}
                </button>
              ))}
            </div>
            <label className="flex items-center gap-1.5 mt-2 text-[10px] cursor-pointer">
              <input
                type="checkbox"
                checked={applyAutoTags}
                onChange={e => setApplyAutoTags(e.target.checked)}
                data-testid="edit-apply-auto-tags"
              />
              {bg
                ? "Приложи автоматични тагове (обединяване с ръчните)"
                : "Apply auto-tags (union with picked)"}
            </label>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[11px] font-semibold">{bg ? "Състояние" : "State"}</label>
              <select value={state} onChange={e => setState(e.target.value)}
                className="w-full h-9 rounded-md border text-xs px-2 bg-white dark:bg-gray-900"
                data-testid="edit-state">
                <option value="raw">{bg ? "Сурово" : "Raw"}</option>
                <option value="cooked">{bg ? "Готвено" : "Cooked"}</option>
                <option value="fermented">{bg ? "Ферментирало" : "Fermented"}</option>
                <option value="dried">{bg ? "Изсушено" : "Dried"}</option>
                <option value="pickled">{bg ? "Мариновано" : "Pickled"}</option>
                <option value="canned">{bg ? "Консервирано" : "Canned"}</option>
              </select>
            </div>
            <div>
              <label className="text-[11px] font-semibold">{bg ? "Порция (g)" : "Portion (g)"}</label>
              <Input type="number" min="1" max="2000" value={portion}
                onChange={e => setPortion(e.target.value)}
                className="h-9 text-xs" data-testid="edit-portion" />
            </div>
          </div>

          <div>
            <label className="text-[11px] font-semibold">{bg ? "Синоними (запетая)" : "Aliases (comma-separated)"}</label>
            <Input value={aliases} onChange={e => setAliases(e.target.value)}
              className="h-9 text-xs" data-testid="edit-aliases" />
          </div>

          <div>
            <label className="text-[11px] font-semibold">{bg ? "Бележки" : "Notes"}</label>
            <Input value={notes} onChange={e => setNotes(e.target.value)}
              className="h-9 text-xs" data-testid="edit-notes" />
          </div>

          <details className="border rounded-md p-2">
            <summary className="text-[11px] font-semibold cursor-pointer text-orange-700 dark:text-orange-400">
              {bg ? "Корекция на макроси (per 100g) — само при грешна USDA стойност" : "Macro override (per 100g) — only for wrong USDA values"}
            </summary>
            <div className="grid grid-cols-2 gap-2 mt-2">
              <MacroInput label="kcal" value={calories} onChange={setCalories} testid="edit-calories" />
              <MacroInput label="Protein (g)" value={protein} onChange={setProtein} testid="edit-protein" />
              <MacroInput label="Carbs (g)" value={carbs} onChange={setCarbs} testid="edit-carbs" />
              <MacroInput label="Fat (g)" value={fat} onChange={setFat} testid="edit-fat" />
              <MacroInput label="Fiber (g)" value={fiber} onChange={setFiber} testid="edit-fiber" />
              <MacroInput label="Sugar (g)" value={sugar} onChange={setSugar} testid="edit-sugar" />
              <MacroInput label="Sat.Fat (g)" value={satFat} onChange={setSatFat} testid="edit-sat-fat" />
            </div>
            <p className="text-[10px] text-muted-foreground mt-1.5">
              {bg
                ? "При промяна на макроси NIS/DIL се преизчисляват автоматично."
                : "When macros change NIS/DIL are recomputed automatically."}
            </p>
          </details>

          {/* iter722 — Micronutrient override + Refresh from USDA */}
          <details className="border rounded-md p-2">
            <summary className="text-[11px] font-semibold cursor-pointer text-indigo-700 dark:text-indigo-400 flex items-center justify-between">
              <span>{bg ? "Микронутриенти (per 100g)" : "Micronutrients (per 100g)"}</span>
              {record.source_fdc_id ? (
                <button
                  type="button"
                  onClick={async (e) => {
                    e.preventDefault(); e.stopPropagation();
                    if (refreshing) return;
                    setRefreshing(true);
                    try {
                      const r = await axios.post(`${API}/admin/nutrami/${record.id}/refresh-from-usda`);
                      toast.success(
                        bg
                          ? `Обновено от USDA: ${r.data.micronutrient_count} микронутриента, NIS ${r.data.nis?.toFixed?.(0) ?? "—"}`
                          : `Refreshed from USDA: ${r.data.micronutrient_count} micronutrients, NIS ${r.data.nis?.toFixed?.(0) ?? "—"}`
                      );
                      onSuccess?.();
                    } catch (err) {
                      toast.error(err?.response?.data?.detail || (bg ? "USDA грешка" : "USDA fetch failed"));
                    }
                    setRefreshing(false);
                  }}
                  className="text-[10px] font-semibold px-2 py-1 rounded bg-indigo-500 text-white hover:bg-indigo-600 disabled:opacity-50 flex items-center gap-1"
                  disabled={refreshing}
                  data-testid="edit-refresh-from-usda"
                >
                  {refreshing
                    ? <Loader2 className="w-3 h-3 animate-spin" />
                    : <RefreshCw className="w-3 h-3" />}
                  {bg ? "Опресни от USDA" : "Refresh from USDA"}
                </button>
              ) : (
                <span className="text-[9px] text-muted-foreground italic">
                  {bg ? "няма USDA fdc_id" : "no USDA fdc_id"}
                </span>
              )}
            </summary>

            {/* Minerals */}
            <div className="mt-3">
              <p className="text-[10px] font-bold text-gray-500 mb-1.5">{bg ? "Минерали" : "Minerals"}</p>
              <div className="grid grid-cols-2 gap-2">
                <MacroInput label="Sodium (mg)"     value={micros.sodium_mg}     onChange={v => setMicro("sodium_mg", v)}     testid="micro-sodium" />
                <MacroInput label="Potassium (mg)"  value={micros.potassium_mg}  onChange={v => setMicro("potassium_mg", v)}  testid="micro-potassium" />
                <MacroInput label="Calcium (mg)"    value={micros.calcium_mg}    onChange={v => setMicro("calcium_mg", v)}    testid="micro-calcium" />
                <MacroInput label="Iron (mg)"       value={micros.iron_mg}       onChange={v => setMicro("iron_mg", v)}       testid="micro-iron" />
                <MacroInput label="Magnesium (mg)"  value={micros.magnesium_mg}  onChange={v => setMicro("magnesium_mg", v)}  testid="micro-magnesium" />
                <MacroInput label="Phosphorus (mg)" value={micros.phosphorus_mg} onChange={v => setMicro("phosphorus_mg", v)} testid="micro-phosphorus" />
                <MacroInput label="Zinc (mg)"       value={micros.zinc_mg}       onChange={v => setMicro("zinc_mg", v)}       testid="micro-zinc" />
                <MacroInput label="Copper (mg)"     value={micros.copper_mg}     onChange={v => setMicro("copper_mg", v)}     testid="micro-copper" />
                <MacroInput label="Manganese (mg)"  value={micros.manganese_mg}  onChange={v => setMicro("manganese_mg", v)}  testid="micro-manganese" />
                <MacroInput label="Selenium (µg)"   value={micros.selenium_ug}   onChange={v => setMicro("selenium_ug", v)}   testid="micro-selenium" />
              </div>
            </div>

            {/* Vitamins */}
            <div className="mt-3">
              <p className="text-[10px] font-bold text-gray-500 mb-1.5">{bg ? "Витамини" : "Vitamins"}</p>
              <div className="grid grid-cols-2 gap-2">
                <MacroInput label="Vit A (µg)"   value={micros.vit_a_ug}  onChange={v => setMicro("vit_a_ug", v)}  testid="micro-vit-a" />
                <MacroInput label="Vit C (mg)"   value={micros.vit_c_mg}  onChange={v => setMicro("vit_c_mg", v)}  testid="micro-vit-c" />
                <MacroInput label="Vit D (µg)"   value={micros.vit_d_ug}  onChange={v => setMicro("vit_d_ug", v)}  testid="micro-vit-d" />
                <MacroInput label="Vit E (mg)"   value={micros.vit_e_mg}  onChange={v => setMicro("vit_e_mg", v)}  testid="micro-vit-e" />
                <MacroInput label="Vit K (µg)"   value={micros.vit_k_ug}  onChange={v => setMicro("vit_k_ug", v)}  testid="micro-vit-k" />
                <MacroInput label="B1 Thi (mg)"  value={micros.vit_b1_mg} onChange={v => setMicro("vit_b1_mg", v)} testid="micro-vit-b1" />
                <MacroInput label="B2 Rib (mg)"  value={micros.vit_b2_mg} onChange={v => setMicro("vit_b2_mg", v)} testid="micro-vit-b2" />
                <MacroInput label="B3 Nia (mg)"  value={micros.vit_b3_mg} onChange={v => setMicro("vit_b3_mg", v)} testid="micro-vit-b3" />
                <MacroInput label="B6 (mg)"      value={micros.vit_b6_mg} onChange={v => setMicro("vit_b6_mg", v)} testid="micro-vit-b6" />
                <MacroInput label="B12 (µg)"     value={micros.vit_b12_ug} onChange={v => setMicro("vit_b12_ug", v)} testid="micro-vit-b12" />
                <MacroInput label="Folate (µg)"  value={micros.folate_ug} onChange={v => setMicro("folate_ug", v)} testid="micro-folate" />
              </div>
            </div>

            {/* Fatty acids */}
            <div className="mt-3">
              <p className="text-[10px] font-bold text-gray-500 mb-1.5">{bg ? "Мастни киселини" : "Fatty acids"}</p>
              <div className="grid grid-cols-2 gap-2">
                <MacroInput label="Omega-3 (g)"  value={micros.omega3_total_g} onChange={v => setMicro("omega3_total_g", v)} testid="micro-omega3" />
                <MacroInput label="Omega-6 LA (g)" value={micros.omega6_la_g}  onChange={v => setMicro("omega6_la_g", v)}   testid="micro-omega6" />
                <MacroInput label="MUFA (g)"     value={micros.mufa_g}         onChange={v => setMicro("mufa_g", v)}         testid="micro-mufa" />
                <MacroInput label="PUFA (g)"     value={micros.pufa_g}         onChange={v => setMicro("pufa_g", v)}         testid="micro-pufa" />
              </div>
            </div>
            {record.last_usda_refresh && (
              <p className="text-[9px] text-muted-foreground mt-2 italic">
                {bg ? "Последна USDA синхронизация: " : "Last USDA refresh: "}
                {new Date(record.last_usda_refresh).toLocaleString(bg ? "bg-BG" : "en-US")}
              </p>
            )}
          </details>

          <div className="flex gap-2 pt-2">
            <Button variant="outline" onClick={onClose} className="flex-1" data-testid="edit-cancel">
              {bg ? "Отказ" : "Cancel"}
            </Button>
            <Button onClick={submit} disabled={saving}
              className="flex-1 gradient-primary text-white"
              data-testid="edit-submit">
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Check className="w-3.5 h-3.5 mr-1.5" />}
              {bg ? "Запази" : "Save"}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function MacroInput({ label, value, onChange, testid }) {
  return (
    <div>
      <label className="text-[10px] font-semibold text-gray-600 dark:text-gray-400">{label}</label>
      <Input
        type="number" step="0.1" min="0"
        value={value ?? ""}
        onChange={e => onChange(e.target.value)}
        className="h-8 text-xs" data-testid={testid}
      />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// BulkCsvModal — iter956
// Admin uploads a CSV with the same fields ManualAddModal collects. The
// backend validates each row, computes NIS/DIL/inflammation, auto-tags
// KEGG pathways, and returns per-row status (imported / skipped_duplicate
// / errors). Includes a dry-run mode + template download so admins can
// sanity-check before committing.
// ─────────────────────────────────────────────────────────────────────────
function BulkCsvModal({ API, bg, onClose, onSuccess }) {
  const [file, setFile]         = useState(null);
  const [dryRun, setDryRun]     = useState(true);
  const [running, setRunning]   = useState(false);
  const [result, setResult]     = useState(null);

  const onPick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) {
      toast.error(bg ? "Файлът е твърде голям (>5MB)." : "File too large (>5MB).");
      return;
    }
    setFile(f);
    setResult(null);
  };

  const submit = async () => {
    if (!file) {
      toast.error(bg ? "Изберете CSV файл." : "Pick a CSV file.");
      return;
    }
    const fd = new FormData();
    fd.append("file", file);
    fd.append("dry_run", dryRun ? "true" : "false");
    setRunning(true);
    try {
      const r = await axios.post(`${API}/admin/nutrami/bulk-csv-import`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(r.data);
      const d = r.data;
      if (d.errors > 0) {
        toast.error(
          bg
            ? `Импорт приключи с ${d.errors} грешки. Импортирани: ${d.imported}, дубликати: ${d.skipped_duplicate}.`
            : `Import finished with ${d.errors} errors. Imported: ${d.imported}, duplicates: ${d.skipped_duplicate}.`
        );
      } else {
        toast.success(
          bg
            ? `Импорт: ${d.imported} успешни${d.skipped_duplicate ? `, ${d.skipped_duplicate} дубликати` : ""}${d.dry_run ? " (dry-run)" : ""}`
            : `Imported ${d.imported}${d.skipped_duplicate ? `, ${d.skipped_duplicate} duplicates` : ""}${d.dry_run ? " (dry-run)" : ""}`
        );
      }
      if (!d.dry_run && d.imported > 0) onSuccess?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || (bg ? "Грешка при импорт." : "Import error."));
    }
    setRunning(false);
  };

  const downloadTemplate = async () => {
    try {
      const r = await axios.get(`${API}/admin/nutrami/bulk-csv-template`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = "nutrami-bulk-import-template.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(bg ? "Не успях да сваля темплейта." : "Template download failed.");
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="nutrami-csv-modal"
    >
      <Card className="w-full max-w-2xl max-h-[92vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <CardContent className="p-5 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-bold flex items-center gap-2">
              <Database className="w-4 h-4 text-orange-600" />
              {bg ? "Bulk CSV импорт" : "Bulk CSV import"}
            </h3>
            <Button variant="ghost" size="sm" onClick={onClose} data-testid="csv-close-btn">
              <X className="w-4 h-4" />
            </Button>
          </div>

          <div className="p-3 bg-orange-50 dark:bg-orange-950/30 rounded-lg border border-orange-200 dark:border-orange-800">
            <p className="text-[11px] text-orange-800 dark:text-orange-200">
              {bg
                ? "Качете .csv (UTF-8 или CP1251, запетая/точка-запетая). Задължителни колони: name_bg, primary_category, source_type + всички макронутриенти (calories, protein, carbs, fat, fiber, sugar, saturated_fat)."
                : "Upload a .csv (UTF-8 or CP1251, comma/semicolon). Required columns: name_bg, primary_category, source_type + all macros (calories, protein, carbs, fat, fiber, sugar, saturated_fat)."}
            </p>
            <Button
              variant="link"
              size="sm"
              onClick={downloadTemplate}
              className="text-[11px] p-0 h-auto text-orange-700 hover:text-orange-900 mt-1"
              data-testid="csv-template-btn"
            >
              📄 {bg ? "Свали шаблон" : "Download template"}
            </Button>
          </div>

          <div>
            <label className="text-[11px] font-semibold">{bg ? "CSV файл" : "CSV file"}</label>
            <input
              type="file"
              accept=".csv,.txt,text/csv"
              onChange={onPick}
              className="text-xs mt-1 w-full"
              data-testid="csv-file-input"
            />
            {file && (
              <div className="text-[10px] text-muted-foreground mt-1">
                {file.name} · {(file.size / 1024).toFixed(1)} KB
              </div>
            )}
          </div>

          <label className="flex items-center gap-2 cursor-pointer text-[11px]">
            <input
              type="checkbox"
              checked={dryRun}
              onChange={e => setDryRun(e.target.checked)}
              data-testid="csv-dry-run"
            />
            <span className="font-semibold">
              {bg ? "Dry-run (само валидация, без запис)" : "Dry-run (validate only, no writes)"}
            </span>
          </label>

          {result && (
            <div className="border-t pt-3 space-y-2" data-testid="csv-results">
              <p className="text-[11px] font-bold uppercase tracking-wider">
                {bg ? "Резултати" : "Results"}
                {result.dry_run && (
                  <span className="ml-2 px-1.5 py-0.5 rounded bg-yellow-100 text-yellow-800 text-[9px] font-bold">
                    DRY-RUN
                  </span>
                )}
              </p>
              <div className="grid grid-cols-3 gap-2">
                <div className="p-2 rounded bg-emerald-50 border border-emerald-200 text-center">
                  <div className="text-lg font-bold text-emerald-700 tabular-nums">{result.imported}</div>
                  <div className="text-[10px] text-emerald-800">{bg ? "Импортирани" : "Imported"}</div>
                </div>
                <div className="p-2 rounded bg-blue-50 border border-blue-200 text-center">
                  <div className="text-lg font-bold text-blue-700 tabular-nums">{result.skipped_duplicate}</div>
                  <div className="text-[10px] text-blue-800">{bg ? "Дубликати" : "Duplicates"}</div>
                </div>
                <div className="p-2 rounded bg-red-50 border border-red-200 text-center">
                  <div className="text-lg font-bold text-red-700 tabular-nums">{result.errors}</div>
                  <div className="text-[10px] text-red-800">{bg ? "Грешки" : "Errors"}</div>
                </div>
              </div>

              {result.detail?.errors?.length > 0 && (
                <div className="max-h-40 overflow-y-auto space-y-1 mt-2">
                  <p className="text-[10px] font-semibold text-red-700">
                    {bg ? "Грешки по редове:" : "Errors by row:"}
                  </p>
                  {result.detail.errors.map((e, i) => (
                    <div
                      key={i}
                      className="text-[10px] p-1.5 rounded bg-red-50 border border-red-100"
                      data-testid={`csv-error-row-${e.row}`}
                    >
                      <span className="font-bold">Row {e.row}</span>
                      {e.name_bg && <span className="ml-1 opacity-70">({e.name_bg})</span>}
                      : <span className="text-red-700">{e.error}</span>
                    </div>
                  ))}
                </div>
              )}

              {result.detail?.imported?.length > 0 && (
                <details className="mt-2">
                  <summary className="cursor-pointer text-[10px] font-semibold text-emerald-700">
                    {bg ? "Виж импортирани" : "Show imported"} ({result.detail.imported.length})
                  </summary>
                  <div className="max-h-40 overflow-y-auto space-y-1 mt-1">
                    {result.detail.imported.map((r, i) => (
                      <div key={i} className="text-[10px] p-1.5 rounded bg-emerald-50 border border-emerald-100">
                        Row {r.row}: <b>{r.name_bg}</b> · NIS {r.nis_percent?.toFixed?.(0) ?? "—"}% · DIL {r.dil_g?.toFixed?.(1) ?? "—"}g · {r.pathway_count} pw ({r.pathway_via})
                      </div>
                    ))}
                  </div>
                </details>
              )}
            </div>
          )}

          <div className="flex items-center justify-end gap-2 border-t pt-3">
            <Button variant="outline" size="sm" onClick={onClose} disabled={running}>
              {bg ? "Затвори" : "Close"}
            </Button>
            <Button
              size="sm"
              onClick={submit}
              disabled={running || !file}
              className="bg-orange-600 hover:bg-orange-700 text-white"
              data-testid="csv-submit-btn"
            >
              {running
                ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> {bg ? "Обработка..." : "Processing..."}</>
                : <><Database className="w-3 h-3 mr-1" /> {dryRun ? (bg ? "Провери" : "Validate") : (bg ? "Импортирай" : "Import")}</>}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
